/**
 * WebCodecs 不可用时的 Opus WASM 下行解码会话。
 * 输入为单声道 48 kHz 裸 Opus 包，输出保持现有播放链需要的双声道交错 Float32 PCM。
 */
interface WasmDecoderHandle {
  decodeFloat(packet: Uint8Array): Float32Array;
  free(): void;
}

export class OpusWasmDownstreamSession {
  private decoder: WasmDecoderHandle | null = null;
  private closed = false;

  constructor(private readonly deviceId: string) {}

  getDeviceId(): string {
    return this.deviceId;
  }

  async ensureConfigured(): Promise<boolean> {
    if (this.closed) {
      return false;
    }
    if (this.decoder) {
      return true;
    }
    if (typeof WebAssembly === 'undefined') {
      console.warn('[WaveLink Opus downstream] 当前环境不支持 WebAssembly', {
        deviceId: this.deviceId,
      });
      return false;
    }

    try {
      const libopus = await import('libopus-wasm');
      const decoder = await libopus.createDecoder({
        sampleRate: 48000,
        channels: 1,
      });
      if (this.closed) {
        decoder.free();
        return false;
      }
      this.decoder = decoder;
      console.warn('[WaveLink Opus downstream] 已启用 WASM Opus 解码降级', {
        deviceId: this.deviceId,
      });
      return true;
    } catch (error) {
      console.error('[WaveLink Opus downstream] WASM Opus 解码器初始化失败', {
        deviceId: this.deviceId,
        error,
      });
      this.decoder = null;
      return false;
    }
  }

  async decodePacket(packet: Uint8Array): Promise<Float32Array> {
    if (!this.decoder || this.closed) {
      throw new Error('WASM Opus decoder not ready');
    }
    if (packet.byteLength === 0) {
      throw new Error('empty opus packet');
    }

    const mono = this.decoder.decodeFloat(packet);
    const stereo = new Float32Array(mono.length * 2);
    for (let i = 0; i < mono.length; i += 1) {
      stereo[i * 2] = mono[i];
      stereo[i * 2 + 1] = mono[i];
    }
    return stereo;
  }

  close(): void {
    this.closed = true;
    try {
      this.decoder?.free();
    } catch (error) {
      console.warn('[WaveLink Opus downstream] 释放 WASM 解码器失败', {
        deviceId: this.deviceId,
        error,
      });
    }
    this.decoder = null;
  }
}
